/**
 * The saved forecast, as the AI Forecast tab expects to read it.
 *
 * A forecast is persisted on the model as `forecastDistribution`, but the tab
 * reads the store's `forecast`, which only a run in the current session ever
 * filled. Reopening a model therefore showed "Run forecast" with the
 * enterprise value range, the posteriors and the methodology all gone, even
 * though the entire distribution was sitting on the row. The obvious response
 * is to run it again, which costs the full pipeline - a minute or two and a
 * batch of LLM calls - to recompute something already stored.
 *
 * The two shapes are the same object at rest: `runForecast` assigns the task's
 * distribution to BOTH fields. What differs is optionality - the stored
 * payload has `methodology` and `inputsPatch` optional - so the gaps are
 * filled rather than the type forced.
 */

import type { ForecastResultV2 } from '../types/financial-model-v2';

/** Null in, null out: a model with no forecast must not look like it has one. */
export function hydrateForecast(stored: unknown): ForecastResultV2 | null {
  if (!stored || typeof stored !== 'object') return null;

  const payload = stored as Partial<ForecastResultV2>;
  // An enterprise value is the one thing every forecast has. Without it there
  // is nothing to show, and a half-populated panel is worse than none.
  if (!payload.enterpriseValue) return null;

  return {
    enterpriseValue: payload.enterpriseValue,
    sensitivity: payload.sensitivity ?? {},
    forecasterSummaries: payload.forecasterSummaries ?? {},
    forecasterWarnings: payload.forecasterWarnings ?? [],
    posteriorSummaries: payload.posteriorSummaries ?? {},
    cacheHit: payload.cacheHit ?? false,
    nSamples: payload.nSamples ?? 0,
    promptVersion: payload.promptVersion ?? '',
    methodology: payload.methodology ?? '',
    inputsPatch: payload.inputsPatch ?? {},
  };
}
